import { DOCUMENT } from '@angular/common';
import { Component, computed, effect, ElementRef, HostListener, inject, NgZone, signal, viewChild, viewChildren } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { ClipboardService } from 'ngx-clipboard';
import { LocalStorageService } from 'ngx-webstorage';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { USE_SUPPORT_RECORDING } from '@phoenix/common';
import { SupportRecordingService } from './support-recording.service';
import { UserPermissionService } from '../../shared/services/user-permission.service';
import { SettingsService } from '../../@core/services/settings.service';
import { S_DEBUG_MODE } from '../../@core/constants/settings-keys';
import { BarPosition, clampBarPosition, descriptionMissing, formatElapsed, movedBeyond, parseBarPosition, SUPPORT_REC_BAR_KEY } from './support-rec';

/** Size of the bar before it was measured, to keep a stored position inside a smaller window. */
const BAR_SIZE_GUESS = { width: 240, height: 40 };

/**
 * Support recording: window from the user menu with "Start recording" (grey while off; red with REC and the
 * running time while on), the required description and "Send to support"; once sent, the ticket of the support with a
 * copy button instead of the description. "Create report" only in debug mode. All as wide as the window. A minus instead of the X minimises it; while a recording runs, a bar then stays on the
 * page and can be dragged with the mouse. Stop in the bar ends the recording and opens the window for the report.
 * Attached once to the overlay host by provideSupportRecording, so it stays across navigation.
 */
/** The reasons the server gives when sending to the support failed, see recording-sender.ts of the plugin. */
const SEND_ERROR_TEXTS: Record<string, string> = {
    'no-license': _('supportRecording.sendErrorNoLicense'),
    off: _('supportRecording.sendErrorOff'),
    unreachable: _('supportRecording.sendErrorUnreachable'),
    'not-supported': _('supportRecording.sendErrorNotSupported'),
    refused: _('supportRecording.sendErrorRefused'),
    'too-many': _('supportRecording.sendErrorTooMany'),
    'too-large': _('supportRecording.sendErrorTooLarge'),
    failed: _('supportRecording.sendErrorFailed'),
};

@Component({
    selector: 'app-support-rec',
    standalone: true,
    imports: [DialogModule, ButtonModule, TextareaModule, TooltipModule, TranslatePipe],
    template: `
        <p-dialog
            [visible]="recorder.supportRecOpen()"
            (visibleChange)="recorder.supportRecOpen.set($event)"
            [header]="'supportRecording.title' | translate"
            [modal]="false"
            position="bottomleft"
            [draggable]="false"
            [resizable]="false"
            closeIcon="fa fa-minus"
            [closeAriaLabel]="'supportRecording.minimize' | translate"
            [style]="{ width: '21rem' }"
            styleClass="support-rec-dialog"
        >
            <div class="flex flex-col gap-3" data-testid="support-rec-panel" data-support-rec-skip>
                <!-- a PrimeNG button like "Create report" and as wide: red with REC and the running time while recording, grey with "Start recording" while off -->
                <button
                    pButton
                    type="button"
                    data-testid="support-rec-toggle"
                    [severity]="running() ? 'danger' : 'secondary'"
                    [fluid]="true"
                    [attr.aria-pressed]="running()"
                    [pTooltip]="'supportRecording.end' | translate"
                    [tooltipDisabled]="!running()"
                    [disabled]="!running() && !mayStart()"
                    (click)="toggle()"
                >
                    <span class="inline-block w-2.5 h-2.5 rounded-full" [class]="running() ? 'bg-white animate-pulse' : 'bg-slate-400'"></span>
                    @if (running()) {
                        <span pButtonLabel>{{ 'supportRecording.rec' | translate }}</span>
                        <span #elapsed class="tabular-nums" data-testid="support-rec-elapsed"></span>
                    } @else {
                        <span pButtonLabel>{{ 'supportRecording.start' | translate }}</span>
                    }
                </button>
                @if (running()) {
                    <!-- the user marks where the problem is; the report starts there -->
                    <button pButton type="button" data-testid="support-rec-mark" severity="warn" [outlined]="true" [fluid]="true" (click)="recorder.mark()">
                        <i class="fa fa-flag"></i>
                        <span pButtonLabel>{{ 'supportRecording.mark' | translate }}</span>
                        @if (recorder.marked()) {
                            <span class="tabular-nums">· {{ 'supportRecording.marked' | translate }} {{ recorder.marked() }}</span>
                        }
                    </button>
                }
                <div class="text-sm text-slate-600 dark:text-slate-300" data-testid="support-rec-info">
                    @if (recorder.rec().running; as running) {
                        {{ 'supportRecording.since' | translate: { time: clock(running.from) } }}
                    } @else if (recorder.rec().last; as last) {
                        {{ 'supportRecording.last' | translate: { from: clock(last.from), to: clock(last.to ?? last.from) } }}
                    }
                </div>
                <!-- debug mode, a new recording runs: the last one stays downloadable (its report is on the server, also after a reload) -->
                @if (debugMode() && recorder.rec().running && recorder.rec().last; as last) {
                    <div class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400" data-testid="support-rec-previous">
                        <span>{{ 'supportRecording.last' | translate: { from: clock(last.from), to: clock(last.to ?? last.from) } }}</span>
                        <button
                            type="button"
                            data-testid="support-rec-previous-report"
                            class="rounded px-1.5 py-0.5 hover:bg-slate-200 dark:hover:bg-slate-700"
                            [pTooltip]="'supportRecording.report' | translate"
                            [attr.aria-label]="'supportRecording.report' | translate"
                            [disabled]="recorder.reportBusy()"
                            (click)="recorder.downloadLast()"
                        >
                            <i class="fa fa-file-arrow-down"></i>
                        </button>
                    </div>
                }
                @if (ticket(); as ticket) {
                    <!-- sent: the ticket of the support takes the place of the description, to name when asking -->
                    <div class="flex flex-col gap-1 text-sm text-slate-600 dark:text-slate-300">
                        <span>{{ 'supportRecording.ticket' | translate }}</span>
                        <div class="flex items-center gap-2 rounded border border-slate-300 bg-slate-50 px-3 py-1.5 dark:border-slate-600 dark:bg-slate-800">
                            <span class="flex-1 select-all font-mono text-base font-semibold text-slate-900 dark:text-slate-100" data-testid="support-rec-ticket">{{ ticket }}</span>
                            <button
                                type="button"
                                data-testid="support-rec-ticket-copy"
                                class="rounded px-1.5 py-0.5 hover:bg-slate-200 dark:hover:bg-slate-700"
                                [pTooltip]="copyTextKey() | translate"
                                [attr.aria-label]="'common.copy' | translate"
                                (click)="copyTicket(ticket)"
                            >
                                <i
                                    class="fa"
                                    [class.fa-copy]="copyState() === 'idle'"
                                    [class.fa-check]="copyState() === 'copied'"
                                    [class.text-green-600]="copyState() === 'copied'"
                                    [class.fa-xmark]="copyState() === 'failed'"
                                    [class.text-red-600]="copyState() === 'failed'"
                                ></i>
                            </button>
                        </div>
                        <span class="text-xs text-slate-500 dark:text-slate-400">{{ 'supportRecording.ticketHint' | translate }}</span>
                    </div>
                } @else if (hasRecording()) {
                    <!-- required words of the user, on purpose for the support; never recorded (the panel is skipped) -->
                    <label class="flex flex-col gap-1 text-sm text-slate-600 dark:text-slate-300">
                        <span>{{ 'supportRecording.description' | translate }}<span class="text-red-500 ml-0.5">*</span></span>
                        <textarea
                            pTextarea
                            rows="3"
                            maxlength="500"
                            required
                            data-testid="support-rec-description"
                            class="w-full"
                            [readonly]="sent()"
                            [value]="recorder.description()"
                            (input)="recorder.description.set($any($event.target).value)"
                        ></textarea>
                        @if (!sent() && missingChars() > 0) {
                            <span class="text-xs text-red-600" data-testid="support-rec-description-short">{{ 'supportRecording.descriptionShort' | translate: { count: missingChars() } }}</span>
                        }
                        <span class="text-xs text-slate-500 dark:text-slate-400">{{ 'supportRecording.descriptionHint' | translate }}</span>
                    </label>
                }
                @if (debugMode()) {
                    <p-button
                        data-testid="support-rec-report"
                        [fluid]="true"
                        icon="fa fa-file-arrow-down"
                        [label]="'supportRecording.report' | translate"
                        [loading]="recorder.reportBusy()"
                        [disabled]="!hasRecording() || missingChars() > 0"
                        (onClick)="recorder.createReport()"
                    />
                }
                <!-- sent once: the button goes, the check line says when; after a failure it stays (nothing arrived) -->
                @if (!sent()) {
                    <p-button
                        data-testid="support-rec-send"
                        [fluid]="true"
                        [outlined]="true"
                        icon="fa fa-paper-plane"
                        [label]="'supportRecording.send' | translate"
                        [loading]="recorder.sendBusy()"
                        [disabled]="!hasRecording() || missingChars() > 0"
                        (onClick)="recorder.sendToSupport()"
                    />
                }
                @if (!running() && recorder.rec().last?.sentAt; as sentAt) {
                    <p class="m-0 text-xs text-green-700 dark:text-green-400" data-testid="support-rec-sent">
                        <i class="fa fa-check"></i> {{ 'supportRecording.sent' | translate: { time: clock(sentAt) } }}
                    </p>
                }
                @if (sendErrorKey(); as key) {
                    <p class="m-0 text-xs text-red-600" data-testid="support-rec-send-error">{{ key | translate }}</p>
                }
                <p class="m-0 text-xs text-slate-500 dark:text-slate-400">{{ 'supportRecording.hint' | translate }}</p>
                @if (recorder.reportFailed()) {
                    <p class="m-0 text-xs text-red-600" data-testid="support-rec-error">{{ 'supportRecording.error' | translate }}</p>
                }
                @if (recorder.descriptionFailed()) {
                    <p class="m-0 text-xs text-red-600" data-testid="support-rec-description-error">{{ 'supportRecording.descriptionError' | translate }}</p>
                }
            </div>
        </p-dialog>

        @if (running() && !recorder.supportRecOpen()) {
            <div
                #bar
                data-testid="support-rec-bar"
                data-support-rec-skip
                class="fixed z-[10000] flex items-center gap-3 rounded-full bg-slate-900/90 px-4 py-2 text-sm text-white shadow-lg cursor-move touch-none"
                [class.bottom-16]="!barPos()"
                [class.left-4]="!barPos()"
                [class.select-none]="dragging()"
                [style.left.px]="barPos()?.left ?? null"
                [style.top.px]="barPos()?.top ?? null"
                (pointerdown)="onBarPointerDown($event)"
            >
                <!-- tooltips above the bar and over it: the bar sits at z-index 10000, a PrimeNG tooltip (1100, right of its button) would lie behind it -->
                <button
                    type="button"
                    data-testid="support-rec-bar-open"
                    class="flex items-center gap-2 cursor-move"
                    [pTooltip]="'supportRecording.open' | translate"
                    tooltipPosition="top"
                    tooltipZIndex="10001"
                    [tooltipDisabled]="dragging()"
                    (click)="recorder.openSupportRec()"
                >
                    <span class="inline-block w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></span>
                    <span class="font-semibold">{{ 'supportRecording.rec' | translate }}</span>
                    <span #elapsed class="tabular-nums"></span>
                </button>
                <button
                    type="button"
                    data-testid="support-rec-bar-mark"
                    class="flex items-center gap-1.5 rounded-full bg-amber-500/80 px-3 py-0.5 hover:bg-amber-500"
                    [pTooltip]="'supportRecording.mark' | translate"
                    tooltipPosition="top"
                    tooltipZIndex="10001"
                    [attr.aria-label]="'supportRecording.mark' | translate"
                    (click)="recorder.mark()"
                >
                    <i class="fa fa-flag text-xs"></i>
                    @if (recorder.marked()) {
                        <span class="tabular-nums">{{ recorder.marked() }}</span>
                    }
                </button>
                <button type="button" data-testid="support-rec-bar-stop" class="flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-0.5 hover:bg-white/25" (click)="stopFromBar()">
                    <i class="fa fa-stop text-xs"></i>
                    {{ 'supportRecording.stop' | translate }}
                </button>
            </div>
        }
    `,
})
export class SupportRecComponent {
    readonly recorder = inject(SupportRecordingService);
    /** The role switch: without it a stopped recording can still be sent, but no new one started. */
    readonly mayStart = inject(UserPermissionService).hasPermission(USE_SUPPORT_RECORDING);
    /** "Create report" (download) only in the debug mode of the profile, live when it is switched; it is no protection, every user can switch it. */
    private readonly debugSetting = inject(SettingsService).getSettingAsSignal<boolean>(S_DEBUG_MODE, false);
    readonly debugMode = computed(() => this.debugSetting() === true);
    private readonly storage = inject(LocalStorageService);
    private readonly document = inject(DOCUMENT);
    private readonly zone = inject(NgZone);
    private readonly bar = viewChild<ElementRef<HTMLElement>>('bar');
    private readonly elapsed = viewChildren<ElementRef<HTMLElement>>('elapsed');

    readonly running = computed(() => Boolean(this.recorder.recording()));
    readonly hasRecording = computed(() => Boolean(this.recorder.rec().running || this.recorder.rec().last));
    /** The description is required (one or two sentences): until it is long enough neither "Create report" nor "Send to support" is possible. */
    readonly missingChars = computed(() => descriptionMissing(this.recorder.description()));
    /** The recording of the window went to the support: it is not sent again, its description stays as it went out. */
    readonly sent = computed(() => !this.running() && Boolean(this.recorder.rec().last?.sentAt));
    /** The ticket of the support for the sent recording; a license server without tickets gives none, the description then stays. */
    readonly ticket = computed(() => (this.sent() ? this.recorder.rec().last?.ticket : undefined));
    /** The copy button shows for a moment whether copying worked. */
    readonly copyState = signal<'idle' | 'copied' | 'failed'>('idle');
    readonly copyTextKey = computed(() =>
        this.copyState() === 'copied' ? _('common.copied-to-clipboard') : this.copyState() === 'failed' ? _('common.copied-to-clipboard-error') : _('common.copy'),
    );
    private readonly clipboard = inject(ClipboardService);
    private copyTimer: ReturnType<typeof setTimeout> | undefined;
    readonly lastSendError = computed(() => (this.running() ? undefined : this.recorder.rec().last?.sendError));
    /** Text for why sending failed: the reason of the server, or that this installation did not answer. */
    readonly sendErrorKey = computed(() => {
        const reason = this.lastSendError();
        if (reason) return SEND_ERROR_TEXTS[reason] ?? SEND_ERROR_TEXTS['failed'];
        return this.recorder.sendFailed() ? SEND_ERROR_TEXTS['failed'] : undefined;
    });

    /** Where the bar was dragged to; without a stored position it sits bottom left above the sidebar footer. */
    readonly barPos = signal<BarPosition | undefined>(this.storedBarPosition());
    readonly dragging = signal(false);

    constructor() {
        // the running time ticks only while a recording runs, outside the NgZone and without change detection:
        // the timer writes the text of the two clocks itself
        effect(onCleanup => {
            const from = this.recorder.rec().running?.from;
            const clocks = this.elapsed();
            if (!from) return;
            const tick = () => {
                const text = formatElapsed(Date.now() - Date.parse(from));
                for (const c of clocks) c.nativeElement.textContent = text;
            };
            tick();
            const timer = this.zone.runOutsideAngular(() => setInterval(tick, 1000));
            onCleanup(() => clearInterval(timer));
        });
    }

    toggle(): void {
        if (this.running()) void this.recorder.stopRec();
        else if (this.mayStart()) void this.recorder.startRec();
    }

    /** Stop in the bar: the recording ends and the window opens with "Create report". */
    stopFromBar(): void {
        void this.recorder.stopRec();
        this.recorder.supportRecOpen.set(true);
    }

    /** Copies the ticket: the clipboard of the browser, otherwise ngx-clipboard (hidden textarea), like the lists of PHX. */
    async copyTicket(ticket: string): Promise<void> {
        let copied = false;
        try {
            await navigator.clipboard.writeText(ticket);
            copied = true;
        } catch {
            copied = this.clipboard.copyFromContent(ticket);
        }
        this.copyState.set(copied ? 'copied' : 'failed');
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => this.copyState.set('idle'), 1500);
    }

    /** Clock time of the browser (24 h like the report), with the day when it is not today. */
    clock(iso: string): string {
        const d = new Date(iso);
        const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
        return d.toDateString() === new Date().toDateString() ? time : `${d.toLocaleDateString([], { day: '2-digit', month: '2-digit' })} ${time}`;
    }

    /**
     * Drags the bar with the mouse (pattern of the action performance panel). A press becomes a drag only beyond
     * a few pixels, so a click on REC or Stop stays a click; the click that ends a drag is swallowed.
     */
    onBarPointerDown(event: PointerEvent): void {
        const bar = this.bar()?.nativeElement;
        const view = this.document.defaultView;
        if (event.button !== 0 || !bar || !view) return;
        const rect = bar.getBoundingClientRect();
        const startX = event.clientX;
        const startY = event.clientY;
        let moved = false;

        const onMove = (move: PointerEvent) => {
            const dx = move.clientX - startX;
            const dy = move.clientY - startY;
            if (!moved && !movedBeyond(dx, dy)) return;
            moved = true;
            this.dragging.set(true);
            this.barPos.set(clampBarPosition({ left: rect.left + dx, top: rect.top + dy }, rect, { width: view.innerWidth, height: view.innerHeight }));
        };
        const onUp = () => {
            view.removeEventListener('pointermove', onMove);
            view.removeEventListener('pointerup', onUp);
            view.removeEventListener('pointercancel', onUp);
            if (!moved) return;
            this.dragging.set(false);
            this.saveBarPosition();
            // the click that follows the release must not stop the recording, open the window or become a step of the
            // report; released outside the bar, the browser sends it to a common ancestor (html), so catch it on the window
            const swallow = (click: Event) => {
                click.stopPropagation();
                click.preventDefault();
            };
            view.addEventListener('click', swallow, { capture: true, once: true });
            view.setTimeout(() => view.removeEventListener('click', swallow, { capture: true }), 0);
        };
        view.addEventListener('pointermove', onMove);
        view.addEventListener('pointerup', onUp);
        view.addEventListener('pointercancel', onUp);
    }

    /** A smaller window keeps the bar inside. */
    @HostListener('window:resize')
    onResize(): void {
        const pos = this.barPos();
        const view = this.document.defaultView;
        if (!pos || !view) return;
        const rect = this.bar()?.nativeElement.getBoundingClientRect() ?? BAR_SIZE_GUESS;
        this.barPos.set(clampBarPosition(pos, rect, { width: view.innerWidth, height: view.innerHeight }));
    }

    private storedBarPosition(): BarPosition | undefined {
        try {
            const pos = parseBarPosition(this.storage.retrieve(SUPPORT_REC_BAR_KEY));
            const view = this.document.defaultView;
            return pos && view ? clampBarPosition(pos, BAR_SIZE_GUESS, { width: view.innerWidth, height: view.innerHeight }) : pos;
        } catch {
            return undefined;
        }
    }

    private saveBarPosition(): void {
        try {
            this.storage.store(SUPPORT_REC_BAR_KEY, this.barPos());
        } catch {
            // only a convenience: the bar works without a stored position
        }
    }
}
